import { defineStore } from 'pinia';
import axios from 'axios';
import { SecureStorage } from '@/utils/storage';

const BaseURL = process.env.VUE_APP_API_BASE_URL;
const CACHE_KEY = 'app_personalities';

export const usePublicPersonalityStore = defineStore('publicPersonality', {
  state: () => ({
    personalitiesList: [],
    currentPersonality: null,
    isLoading: false,
    error: null,
    lastFetchTime: null,
    lastFetchedLang: null,
  }),
  actions: {
    async fetchPersonalitiesList(lang = 'en') {
      if (this.isLoading) return;

      if (this.personalitiesList.length > 0 && this.lastFetchedLang === lang) {
        const oneHour = 60 * 60 * 1000;
        if ((Date.now() - this.lastFetchTime) < oneHour) return;
      }

      const cached = SecureStorage.getItem(CACHE_KEY);
      if (cached && cached.lang === lang && Array.isArray(cached.data) && cached.data.length > 0) {
        this.personalitiesList = cached.data;
        this.lastFetchTime = cached.time;
        this.lastFetchedLang = lang;
        return;
      }

      this.isLoading = true;
      this.error = null;

      try {
        const headers = { 'Accept-Language': lang };
        const response = await axios.get(`${BaseURL}/public-personalities/`, { headers });

        const contactPromises = response.data.map(p =>
          axios.get(`${BaseURL}/public-personality-contacts/${p.id}/`, { headers }).catch(() => ({ data: {} }))
        );
        const contactResponses = await Promise.all(contactPromises);

        this.personalitiesList = response.data.map((person, index) => {
          const socialData = contactResponses[index].data;
          const transformSocialMedia = (sd) => {
            const platformMap = {
              facebook_url: 'facebook', twitter_url: 'twitter', instagram_url: 'instagram',
              youtube: 'youtube', linkedin_url: 'linkedin', email: 'email', tiktok_url: 'tiktok'
            };
            const validLinks = [];
            for (const key in platformMap) {
              if (sd && sd[key]) {
                const url = key === 'email' ? `mailto:${sd[key]}` : sd[key];
                validLinks.push({ platform: platformMap[key], url });
              }
            }
            return validLinks;
          };

          return {
            ...person,
            photo: person.photo || '/assets/images/default-avatar.png',
            validSocialMedia: transformSocialMedia(socialData),
            contacts: socialData,
          };
        });

        this.lastFetchTime = Date.now();
        this.lastFetchedLang = lang;

        SecureStorage.setItem(CACHE_KEY, {
          data: this.personalitiesList,
          lang,
          time: Date.now(),
        }, SecureStorage.TTL.PERSONALITY);
      } catch {
        this.error = 'Failed to load personalities. Please try again later.';
      } finally {
        this.isLoading = false;
      }
    },

    setCurrentPersonality(personalityData) {
      this.currentPersonality = personalityData;
    },

    clearCurrentPersonality() {
      this.currentPersonality = null;
    },
  },
  getters: {
    hasCurrentPersonality: (state) => !!state.currentPersonality,
  },
});
